import { useMemo } from "react"
import { FACTOR_RULE_DISPLAY_TYPES } from "components/search-factor/constants"
import { useRules } from "./use-rules"
import type { PositionSetInput, FactorRuleDisplay } from "../types"
import { createRuleKey } from "../helpers"

type UseFilteredRulesResult = [
  FactorRuleDisplay[],
  FactorRuleDisplay[],
  boolean,
  boolean,
  Error | null,
  Error | null,
]

function getFilteredRules(rules: FactorRuleDisplay[], modeledRules: FactorRuleDisplay[]) {
  const modeledRulesMap = new Map(modeledRules.map(rule => [createRuleKey(rule), rule]))

  return rules.reduce((acc, existingRule) => {
    const foundRule = modeledRulesMap.get(createRuleKey(existingRule))
    if (foundRule) {
      // Only create new object if values actually changed
      if (existingRule.value === foundRule.value
          && existingRule.activeValue === foundRule.activeValue) {
        acc.push(existingRule)
      } else {
        acc.push({ ...existingRule, value: foundRule.value, activeValue: foundRule.activeValue })
      }
    }
    return acc
  }, [] as FactorRuleDisplay[])
}

export function useFilteredRules(
  positionSet: PositionSetInput, basePositionSet: PositionSetInput | null | undefined, exposureRules: FactorRuleDisplay[], compositionRules: FactorRuleDisplay[]):
UseFilteredRulesResult {
  const [modeledExposureRules, isExposureRulesQueryReady, exposureRulesError] = useRules(positionSet, basePositionSet, FACTOR_RULE_DISPLAY_TYPES.EXPOSURE, [])
  const [modeledCompositionRules, isCompositionRulesQueryReady, compositionRulesError] = useRules(positionSet, basePositionSet, FACTOR_RULE_DISPLAY_TYPES.COMPOSITION, [])

  const filteredExposureRules = useMemo(() => {
    if (!isExposureRulesQueryReady || exposureRulesError) return exposureRules
    if (modeledExposureRules.length === 0 && exposureRules.length > 0) {
      return []
    }
    return getFilteredRules(exposureRules, modeledExposureRules)
  }, [modeledExposureRules, exposureRules, isExposureRulesQueryReady, exposureRulesError])

  const filteredCompositionRules = useMemo(() => {
    if (!isCompositionRulesQueryReady || compositionRulesError) return compositionRules
    if (modeledCompositionRules.length === 0 && compositionRules.length > 0) {
      return []
    }
    return getFilteredRules(compositionRules, modeledCompositionRules)
  }, [modeledCompositionRules, compositionRules, isCompositionRulesQueryReady, compositionRulesError])

  return [filteredExposureRules, filteredCompositionRules, isExposureRulesQueryReady, isCompositionRulesQueryReady, exposureRulesError, compositionRulesError]
}
